import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SwiperSlide } from "swiper/react";
import PosSwiper from "../pos-swiper/PosSwiper";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import { Box } from "@mui/material";

import usePricing from "../../../../hooks/usePricing";
import { ALTERATION_SERVICE_ID } from "../../../../hooks/useAlterationPricing";
import { CategoryResponse } from "../../../../typings";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { CartSliceAction } from "../../../../redux/slice/cartSlice";
import ServiceTabButton from "./service-tab-button";
import { Colors } from "../../../../configs/colors";

// Service buttons are 9.85rem wide with 0.57rem gaps; six fit beside the
// pinned Alteration tab in the Add Items panel
const SERVICES_PER_PAGE = 6;
const SWIPER_WIDTH = `${SERVICES_PER_PAGE * 9.85 + (SERVICES_PER_PAGE - 1) * 0.57}rem`;

export interface ICheckInTabMenuProps {
  currentTab: String;
}

const ServiceTabMenu: FC<ICheckInTabMenuProps> = () => {
  const { categoryList } = usePricing();
  const { selectedCartItem } = useAppSelector((state) => state.cart);

  const { selectedServiceType, previousServiceType, selectedSession } =
    useAppSelector((state) => state.cart);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const body = categoryList?.body;
    if (
      selectedServiceType?.categoryName?.trim() === "HH Alteration" &&
      Array.isArray(body) &&
      body.length > 0
    ) {
      const dryClean = body.find((c) => c.categoryName === "Dry Clean");
      if (dryClean) {
        dispatch(CartSliceAction.setSelectedServiceType(dryClean));
      }
    }
  }, [categoryList?.body, selectedServiceType, dispatch]);

  // const setPrev = () => {
  //   return promiseHooks();
  // }

  //create a function to return a promise and dispatch setPreviousServiceType
  // const setPrevService = () => {
  //   return new Promise((resolve, reject) => {
  //     dispatch(CartSliceAction.setPreviousServiceType(selectedServiceType));
  //     resolve("done");
  //   });
  // };

  const { categoryRelationshipResponse, unavailableMenuItems } = useAppSelector(
    (state) => state.pricing
  );

  // A service tab is shown only when this customer's price list offers at
  // least one of its items (e.g. a service priced only in another list stays
  // hidden). Until the menu data loads, fall back to showing it.
  const servicesWithItems = useMemo(() => {
    if (!categoryRelationshipResponse?.body) return null;
    const hidden = new Set(
      (unavailableMenuItems?.styles ?? []).map(
        (s) => `${s.categoryId}:${s.subCategoryId}:${s.secondSubCategoryId}`
      )
    );
    return new Set(
      categoryRelationshipResponse.body
        .filter(
          (r) => !hidden.has(`${r.categoryId}:${r.subCategoryId}:${r.secondSubCategoryId}`)
        )
        .map((r) => r.categoryId)
    );
  }, [categoryRelationshipResponse, unavailableMenuItems]);

  const services = categoryList?.body ?? [];
  const serviceById = (id?: number) => services.find((c) => c.id === id);
  // The service an alteration would belong to (the previous one while the
  // Alteration tab itself is selected)
  const alterationBaseService =
    selectedServiceType?.categoryName === "Alteration"
      ? previousServiceType
      : selectedServiceType;
  // Alterations are offered for services linked to an alteration category
  const serviceOffersAlteration = Boolean(
    serviceById(alterationBaseService?.id)?.alterationCategoryId
  );
  // Alterations are only added on top of an item already in the cart (with
  // "Alteration Only" in Special Requests when the base service isn't wanted),
  // so the tab needs a finalized item selected. A style configured with a
  // pieceCount > 1 (e.g. a 2pc Suit) represents more than one physical
  // garment - alteration is priced/tracked per single piece, so items in a
  // multi-piece style can't be sent into the Alteration add-on flow.
  const alterationEnabled =
    serviceOffersAlteration &&
    Boolean(selectedCartItem?.finalized) &&
    (selectedCartItem?.garmentStyle?.pieceCount ?? 1) <= 1;
  const isAlterationSelected = selectedServiceType?.categoryName === "Alteration";
  const alterationDisabledHint = !serviceOffersAlteration
    ? "Alterations aren't offered for this service"
    : !selectedCartItem?.finalized
      ? "Add and select an item first to add an alteration"
      : "Alterations can't be added to multi-piece items";

  // The selection clears once an alteration is attached (or the row is
  // deselected); leave the tab then so its garment buttons can't start a
  // standalone Alteration item
  useEffect(() => {
    if (!isAlterationSelected || alterationEnabled) return;
    // Back to the ticket's own service: the only enabled tab while it has
    // items. Looked up by id in the menu so the tab highlights even when the
    // item's copy differs (drafts / edited bills); previousServiceType isn't
    // reliable, as restoring a draft or switching tickets also sets it.
    const ticketServiceId = selectedSession?.cartItems?.find(
      (item) => (item.serviceType?.id ?? 0) > 0
    )?.serviceType?.id;
    const baseService =
      services.find((c) => c.id === ticketServiceId) ??
      services.find(
        (c) => c.id === previousServiceType?.id && c.id !== ALTERATION_SERVICE_ID
      ) ??
      services.find((c) => c.showInMenu);
    dispatch(CartSliceAction.setSelectedServiceType(baseService ?? null));
  }, [isAlterationSelected, alterationEnabled]);

  const handleOnClick = async (service: CategoryResponse) => {
    // dispatch(CartSliceAction.setPreviousServiceType(selectedServiceType));
    // await setPrevService().then((res) => {
    //   if (res === "done") {
    dispatch(CartSliceAction.setSelectedServiceType(service));
    //   }
    // });
  };
  const visibleServices = [...services]
    .sort((a, b) => (a.orderIndex ?? 999) - (b.orderIndex ?? 999))
    .filter((tab) => tab.showInMenu && (!servicesWithItems || servicesWithItems.has(tab.id)));
  const pages = Array.from(
    { length: Math.ceil(visibleServices.length / SERVICES_PER_PAGE) },
    (_, page) => visibleServices.slice(page * SERVICES_PER_PAGE, (page + 1) * SERVICES_PER_PAGE)
  );

  // Keep the selected service on screen (e.g. after switching customers)
  const swiperRef = useRef<SwiperType | null>(null);
  const selectedIndex = visibleServices.findIndex(
    (tab) => tab.categoryName === alterationBaseService?.categoryName
  );
  useEffect(() => {
    if (selectedIndex >= 0) swiperRef.current?.slideTo(Math.floor(selectedIndex / SERVICES_PER_PAGE));
  }, [selectedIndex]);

  // Which edges have another page, for the fade + chevron cues
  const [edges, setEdges] = useState({ hasPrev: false, hasNext: false });
  const syncEdges = useCallback((swiper: SwiperType) => {
    const hasPrev = !swiper.isBeginning;
    const hasNext = !swiper.isEnd;
    setEdges((prev) => (prev.hasPrev === hasPrev && prev.hasNext === hasNext ? prev : { hasPrev, hasNext }));
  }, []);
  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper) return;
    swiper.update();
    syncEdges(swiper);
  }, [pages.length, syncEdges]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: "0.57rem",
        alignItems: "center",
      }}
    >
      {/* One row of services, swiped a page at a time like the garment and
          style sections. An edge with another page darkens the edge tab
          into shadow. Alteration stays pinned on the right. */}
      <Box
        data-testid="service-row"
        data-has-prev={edges.hasPrev}
        data-has-next={edges.hasNext}
        sx={{ position: "relative", width: SWIPER_WIDTH, flexShrink: 0 }}
      >
        <PosSwiper
          style={{ width: SWIPER_WIDTH, margin: 0 }}
          spaceBetween={8}
          direction="horizontal"
          onSwiper={(swiper: SwiperType) => {
            swiperRef.current = swiper;
            syncEdges(swiper);
          }}
          // A scrollbar drag or wheel moves the row without a slideChange;
          // progress fires after every move with isBeginning/isEnd current
          // (syncEdges skips unchanged values)
          onSlideChange={syncEdges}
          onProgress={syncEdges}
          onResize={syncEdges}
        >
          {pages.map((pageServices, pageIndex) => (
            <SwiperSlide
              key={pageIndex}
              style={{ display: "flex", flexDirection: "row", gap: "0.57rem", width: SWIPER_WIDTH }}
            >
              {pageServices.map((tab) => (
                <ServiceTabButton
                  name={tab.categoryName}
                  isSelected={selectedServiceType?.categoryName === tab.categoryName}
                  handleOnClick={() => handleOnClick(tab)}
                  key={tab.id}
                  disabled={
                    (selectedSession?.cartItems?.length || 0) > 0 &&
                    selectedSession?.cartItems[0]?.serviceType?.categoryName !==
                      tab.categoryName
                  }
                />
              ))}
            </SwiperSlide>
          ))}
        </PosSwiper>
        <EdgeShade side="left" visible={edges.hasPrev} />
        <EdgeShade side="right" visible={edges.hasNext} />
      </Box>
      <Box
        sx={{
          position: "relative",
          flexShrink: 0,
          // Divider drawn inside the existing gap: Alteration is an add-on to
          // the selected service, not another service
          "&::before": {
            content: '""',
            position: "absolute",
            left: "-0.36rem",
            top: "12%",
            bottom: "12%",
            width: "0.14rem",
            borderRadius: "0.07rem",
            background: Colors.ACCENT_GRAY_DISABLED,
          },
        }}
      >
        <ServiceTabButton
          variant="addon"
          name="Alteration"
          subLabel={
            alterationBaseService?.categoryName && alterationBaseService.categoryName !== "Alteration"
              ? alterationBaseService.categoryName
              : undefined
          }
          isSelected={isAlterationSelected}
          handleOnClick={() => {
            handleOnClick({
              id: -100000,
              categoryName: "Alteration",
              deleted: false,
              showInMenu: true,
            });
          }}
          disabled={!alterationEnabled}
          disabledHint={alterationDisabledHint}
        />
      </Box>
    </Box>
  );
};

/**
 * "More this way" cue: the edge tab darkens toward the edge, as if curving
 * into shadow. Plain black at low strength inside the tab's rounded shape,
 * so it reads as a deeper shade of the tab's own colour. Display only - taps
 * reach the tab under it; the row is swiped.
 */
const EdgeShade: FC<{ side: "left" | "right"; visible: boolean }> = ({ side, visible }) => (
  <Box
    aria-hidden
    sx={{
      position: "absolute",
      top: 0,
      bottom: 0,
      [side]: 0,
      zIndex: 2,
      width: "4.3rem",
      pointerEvents: "none",
      borderRadius: side === "left" ? "0.71rem 0 0 0.71rem" : "0 0.71rem 0.71rem 0",
      background: `linear-gradient(to ${side === "left" ? "right" : "left"}, rgba(0, 0, 0, 0.42), rgba(0, 0, 0, 0.12) 55%, rgba(0, 0, 0, 0))`,
      opacity: visible ? 1 : 0,
      transition: "opacity 160ms ease-out",
      "@media (prefers-reduced-motion: reduce)": { transition: "none" },
    }}
  />
);

export default ServiceTabMenu;
